'use client';

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AnalyzeDamageResponse, DamageAnalysisResult } from '@/types/ai';
import { apiClient } from '@/lib/api-client';

interface UseAIDamageAnalysisResult {
  analyzing: boolean;
  error: Error | null;
  result: AnalyzeDamageResponse | null;
  analyzeDamage: (inspectionId: string, photoIds: string[]) => Promise<void>;
  reset: () => void;
}

/**
 * Legacy hook - kept for backwards compatibility
 * @deprecated Use useAIAnalysisWorkflow instead for better state management
 */
export function useAIDamageAnalysis(): UseAIDamageAnalysisResult {
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [result, setResult] = useState<AnalyzeDamageResponse | null>(null);

  const analyzeDamage = async (inspectionId: string, photoIds: string[]) => {
    try {
      setAnalyzing(true);
      setError(null);

      const response = await apiClient.analyzeDamage(inspectionId, photoIds);
      setResult(response);
    } catch (err) {
      setError(err as Error);
      console.error('AI damage analysis error:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  const reset = () => {
    setAnalyzing(false);
    setError(null);
    setResult(null);
  };

  return {
    analyzing,
    error,
    result,
    analyzeDamage,
    reset,
  };
}

// ========== Modern TanStack Query Hooks ==========

export interface AIAnalysisStatus {
  task_id: string;
  inspection_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  result?: DamageAnalysisResult;
  error?: string;
  created_at: string;
  completed_at?: string;
}

export interface TriggerAnalysisRequest {
  inspection_id: string;
  photo_ids?: string[] | undefined;
  provider?: 'tractable' | 'aws' | 'auto' | undefined;
  priority?: number | undefined;
}

/**
 * Trigger AI damage analysis for an inspection
 */
async function triggerAnalysis(request: TriggerAnalysisRequest): Promise<{ task_id: string }> {
  const response = await fetch('/api/v1/ai/assess', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      inspection_id: request.inspection_id,
      provider: request.provider || 'auto',
      priority: request.priority || 5,
      options: {
        photo_ids: request.photo_ids,
      },
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to trigger AI analysis');
  }

  return response.json();
}

/**
 * Get AI analysis status
 */
async function getAnalysisStatus(taskId: string): Promise<AIAnalysisStatus> {
  const response = await fetch(`/api/v1/ai/assess/${taskId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch analysis status');
  }

  return response.json();
}

/**
 * Get analysis results for an inspection
 */
async function getInspectionAnalysis(inspectionId: string): Promise<DamageAnalysisResult[]> {
  const response = await fetch(`/api/v1/inspections/${inspectionId}/analysis`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch analysis results');
  }

  return response.json();
}

/**
 * Hook to trigger AI damage analysis
 */
export function useTriggerAnalysis() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: triggerAnalysis,
    onSuccess: () => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['ai-analysis'] });
    },
  });
}

/**
 * Hook to get AI analysis status with auto-polling
 */
export function useAnalysisStatus(taskId: string | null, options?: {
  enabled?: boolean;
  refetchInterval?: number;
}) {
  return useQuery({
    queryKey: ['ai-analysis-status', taskId],
    queryFn: () => getAnalysisStatus(taskId!),
    enabled: !!taskId && (options?.enabled !== false),
    refetchInterval: (query) => {
      const data = query.state.data as AIAnalysisStatus | undefined;

      // Stop polling if completed or failed
      if (!data || data.status === 'completed' || data.status === 'failed') {
        return false;
      }

      // Poll every 2 seconds while processing
      return options?.refetchInterval || 2000;
    },
    staleTime: 1000, // Consider data stale after 1 second
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

/**
 * Hook to get analysis results for an inspection
 */
export function useInspectionAnalysis(inspectionId: string | null, options?: {
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: ['inspection-analysis', inspectionId],
    queryFn: () => getInspectionAnalysis(inspectionId!),
    enabled: !!inspectionId && (options?.enabled !== false),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook for complete analysis workflow
 * Triggers analysis and polls for results
 */
export function useAIAnalysisWorkflow(inspectionId: string | null) {
  const trigger = useTriggerAnalysis();
  const status = useAnalysisStatus(
    trigger.data?.task_id || null,
    { enabled: !!trigger.data?.task_id }
  );

  const startAnalysis = async (photoIds?: string[], provider?: 'tractable' | 'aws' | 'auto') => {
    if (!inspectionId) {
      throw new Error('Inspection ID is required');
    }

    return trigger.mutateAsync({
      inspection_id: inspectionId,
      photo_ids: photoIds,
      provider: provider || 'auto',
      priority: 5,
    });
  };

  return {
    startAnalysis,
    isTriggering: trigger.isPending,
    triggerError: trigger.error,
    taskId: trigger.data?.task_id,
    status: status.data,
    isPolling: status.isFetching,
    statusError: status.error,
    isProcessing: status.data?.status === 'processing' || status.data?.status === 'pending',
    isCompleted: status.data?.status === 'completed',
    isFailed: status.data?.status === 'failed',
    result: status.data?.result,
    progress: status.data?.progress,
  };
}
